import React from 'react'
import {
  DownloadSimple,
  Info,
  Warning,
  Calendar,
  CurrencyDollar,
  Clock as PhosphorClock,
  X as PhosphorX,
  CheckCircle,
  Check as PhosphorCheck,
  Upload,
  QuestionMark,
  ArrowLeft as PhosphorArrowLeft,
  PaperPlaneTilt,
  Stop,
  Paperclip,
  BookOpen as PhosphorBookOpen,
  BookmarkSimple,
  MagnifyingGlass,
  Question,
  SidebarSimple,
  User,
  Gear,
  SignOut,
  Buildings,
  House,
  PencilLine as PhosphorPencilLine,
  PencilRuler as PhosphorPencilRuler,
  CreditCard as PhosphorCreditCard,
  Lock,
  DotsThreeVertical,
  Pencil,
  Trash,
  ThumbsUp,
  ThumbsDown,
  Funnel,
  FolderOpen as PhosphorFolderOpen,
  Laptop as PhosphorLaptop,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  VideoCamera,
  ArrowSquareOut,
  Link as PhosphorLink,
  Confetti,
  FileText as PhosphorFileText,
  FilePdf as PhosphorFilePdf,
  PlayCircle as PhosphorPlayCircle,
  ArrowRight as PhosphorArrowRight,
  CurrencyCircleDollar,
  Medal,
  Wrench as PhosphorWrench,
  Sparkle as PhosphorSparkle,
  Microphone as PhosphorMicrophone,
  Envelope,
  PlusCircle as PhosphorPlusCircle,
  Strategy as PhosphorStrategy,
  SlidersHorizontal as PhosphorSlidersHorizontal,
  Users as PhosphorUsers,
  GridFour as PhosphorGridFour,
  ChartBar as PhosphorChartBar,
  ChatCircle as PhosphorChatCircle,
  File as PhosphorFile,
  TextB,
  TextItalic,
  TextH as PhosphorTextH,
  ListBullets as PhosphorListBullets,
  ArrowCounterClockwise as PhosphorArrowCounterClockwise,
  Globe as PhosphorGlobe,
  LinkBreak as PhosphorLinkBreak,
  Share as PhosphorShare,
} from '@phosphor-icons/react'

// Wrapper function to make Phosphor icons compatible with Lucide API
const createIconWrapper = (PhosphorIcon: any, displayName?: string) => {
  const Wrapper = React.forwardRef<SVGSVGElement, any>((props, ref) => {
    const { className, size, weight, ...rest } = props
    // Extract size from className (e.g., "w-4 h-4" -> 16, "w-5 h-5" -> 20)
    let iconSize = size || 24
    if (className) {
      if (className.includes('w-3') || className.includes('h-3')) iconSize = 12
      else if (className.includes('w-4') || className.includes('h-4')) iconSize = 16
      else if (className.includes('w-5') || className.includes('h-5')) iconSize = 20
      else if (className.includes('w-6') || className.includes('h-6')) iconSize = 24
      else if (className.includes('w-8') || className.includes('h-8')) iconSize = 32
    }
    // Icons are decorative by default (WCAG 1.1.1); pass aria-hidden={false} to expose one
    return <PhosphorIcon ref={ref} size={iconSize} weight={weight || 'regular'} className={className} aria-hidden="true" {...rest} />
  })
  
  if (displayName) {
    Wrapper.displayName = displayName
  }
  
  return Wrapper
}

// Export all wrapped icons with Lucide-compatible names
export const Download = createIconWrapper(DownloadSimple, 'Download')
export const AlertCircle = createIconWrapper(Warning, 'AlertCircle')
export const InfoIcon = createIconWrapper(Info, 'Info')
export const CalendarIcon = createIconWrapper(Calendar, 'Calendar')
export const CircleDollarSign = createIconWrapper(CurrencyDollar, 'CircleDollarSign')
export const ClockIcon = createIconWrapper(PhosphorClock, 'Clock')
export const Clock = createIconWrapper(PhosphorClock, 'Clock')
export const X = createIconWrapper(PhosphorX, 'X')
export const CircleCheck = createIconWrapper(CheckCircle, 'CircleCheck')
export const UploadIcon = createIconWrapper(Upload, 'Upload')
export const CircleHelp = createIconWrapper(QuestionMark, 'CircleHelp')
export const ArrowLeftIcon = createIconWrapper(PhosphorArrowLeft, 'ArrowLeftIcon')
export const SendHorizontal = createIconWrapper(PaperPlaneTilt, 'SendHorizontal')
export const CircleStop = createIconWrapper(Stop, 'CircleStop')
export const PaperclipIcon = createIconWrapper(Paperclip, 'Paperclip')
export const Library = createIconWrapper(PhosphorBookOpen, 'Library')
export const BookOpen = createIconWrapper(PhosphorBookOpen, 'BookOpen')
export const Bookmark = createIconWrapper(BookmarkSimple, 'Bookmark')
export const Search = createIconWrapper(MagnifyingGlass, 'Search')
export const HelpCircle = createIconWrapper(Question, 'HelpCircle')
export const PanelLeft = createIconWrapper(SidebarSimple, 'PanelLeft')
export const UserIcon = createIconWrapper(User, 'User')
export const Settings = createIconWrapper(Gear, 'Settings')
export const LogOut = createIconWrapper(SignOut, 'LogOut')
export const Building = createIconWrapper(Buildings, 'Building')
export const Home = createIconWrapper(House, 'Home')
export const PencilLineIcon = createIconWrapper(PhosphorPencilLine, 'PencilLine')
export const CreditCardIcon = createIconWrapper(PhosphorCreditCard, 'CreditCard')
export const LockIcon = createIconWrapper(Lock, 'Lock')
export const MoreVertical = createIconWrapper(DotsThreeVertical, 'MoreVertical')
export const PencilIcon = createIconWrapper(Pencil, 'Pencil')
export const Trash2 = createIconWrapper(Trash, 'Trash2')
export const ThumbsUpIcon = createIconWrapper(ThumbsUp, 'ThumbsUp')
export const ThumbsDownIcon = createIconWrapper(ThumbsDown, 'ThumbsDown')
export const Filter = createIconWrapper(Funnel, 'Filter')
export const FolderOpen = createIconWrapper(PhosphorFolderOpen, 'FolderOpen')
export const Laptop = createIconWrapper(PhosphorLaptop, 'Laptop')
export const ChevronRightIcon = createIconWrapper(CaretRight, 'ChevronRightIcon')
export const Video = createIconWrapper(VideoCamera, 'Video')
export const ExternalLink = createIconWrapper(ArrowSquareOut, 'ExternalLink')
export const LinkIcon = createIconWrapper(PhosphorLink, 'LinkIcon')
export const ArrowLeft = createIconWrapper(PhosphorArrowLeft, 'ArrowLeft')
export const PencilRuler = createIconWrapper(PhosphorPencilRuler, 'PencilRuler')
export const Sparkle = createIconWrapper(PhosphorSparkle, 'Sparkle')
export const CreditCard = createIconWrapper(PhosphorCreditCard, 'CreditCard')
export const FileText = createIconWrapper(PhosphorFileText, 'FileText')
export const FilePdf = createIconWrapper(PhosphorFilePdf, 'FilePdf')
export const ArrowRight = createIconWrapper(PhosphorArrowRight, 'ArrowRight')
export const CircleDollarSignAlt = createIconWrapper(CurrencyCircleDollar, 'CircleDollarSignAlt')
export const Award = createIconWrapper(Medal, 'Award')
export const Check = createIconWrapper(PhosphorCheck, 'Check')
export const ChevronDown = createIconWrapper(CaretDown, 'ChevronDown')
export const ChevronLeft = createIconWrapper(CaretLeft, 'ChevronLeft')
export const ChevronRight = createIconWrapper(CaretRight, 'ChevronRight')
export const ChevronUp = createIconWrapper(CaretUp, 'ChevronUp')
export const PartyPopper = createIconWrapper(Confetti, 'PartyPopper')
export const PlayCircle = createIconWrapper(PhosphorPlayCircle, 'PlayCircle')
export const Wrench = createIconWrapper(PhosphorWrench, 'Wrench')
export const Microphone = createIconWrapper(PhosphorMicrophone, 'Microphone')
export const Mail = createIconWrapper(Envelope, 'Mail')
export const PlusIcon = createIconWrapper(PhosphorPlusCircle, 'PlusIcon')

export const LegacyResources = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 64 63"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M55.125 12.7969 7.875 12.7969C6.24404 12.7969 4.92188 14.119 4.92188 15.75L4.92188 21.6562C4.92188 23.2872 6.24404 24.6094 7.875 24.6094L8.85938 24.6094 8.85938 47.25C8.85938 48.8809 10.1815 50.2031 11.8125 50.2031L51.1875 50.2031C52.8184 50.2031 54.1406 48.8809 54.1406 47.25L54.1406 24.6094 55.125 24.6094C56.7559 24.6094 58.0781 23.2872 58.0781 21.6562L58.0781 15.75C58.0781 14.119 56.7559 12.7969 55.125 12.7969ZM52.1719 47.25C52.1719 47.7936 51.7311 48.2344 51.1875 48.2344L11.8125 48.2344C11.2689 48.2344 10.8281 47.7936 10.8281 47.25L10.8281 24.6094 52.1719 24.6094ZM56.1094 21.6562C56.1094 22.1999 55.6686 22.6406 55.125 22.6406L7.875 22.6406C7.33135 22.6406 6.89062 22.1999 6.89062 21.6562L6.89062 15.75C6.89062 15.2064 7.33135 14.7656 7.875 14.7656L55.125 14.7656C55.6686 14.7656 56.1094 15.2064 56.1094 15.75ZM24.6094 33.4688C24.6094 32.9251 25.0501 32.4844 25.5938 32.4844L37.4062 32.4844C37.9499 32.4844 38.3906 32.9251 38.3906 33.4688 38.3906 34.0124 37.9499 34.4531 37.4062 34.4531L25.5938 34.4531C25.0501 34.4531 24.6094 34.0124 24.6094 33.4688Z" />
  </svg>
)

export const StrategyIcon = createIconWrapper(PhosphorStrategy, 'StrategyIcon')
export const SlidersHorizontal = createIconWrapper(PhosphorSlidersHorizontal, 'SlidersHorizontal')
export const Users = createIconWrapper(PhosphorUsers, 'Users')
export const GridIcon = createIconWrapper(PhosphorGridFour, 'GridIcon')
export const ChartIcon = createIconWrapper(PhosphorChartBar, 'ChartIcon')
export const ChatIcon = createIconWrapper(PhosphorChatCircle, 'ChatIcon')
export const FileIcon = createIconWrapper(PhosphorFile, 'FileIcon')
export const BoldIcon = createIconWrapper(TextB, 'BoldIcon')
export const ItalicIcon = createIconWrapper(TextItalic, 'ItalicIcon')
export const HeadingIcon = createIconWrapper(PhosphorTextH, 'HeadingIcon')
export const ListBulletsIcon = createIconWrapper(PhosphorListBullets, 'ListBulletsIcon')
export const UndoIcon = createIconWrapper(PhosphorArrowCounterClockwise, 'UndoIcon')
export const Globe = createIconWrapper(PhosphorGlobe, 'Globe')
export const LinkBreak = createIconWrapper(PhosphorLinkBreak, 'LinkBreak')
export const ShareIcon = createIconWrapper(PhosphorShare, 'ShareIcon')
